import { useEffect, useState } from "react";

import Container from "@mui/material/Container";
import { useJwtStorage, useLocalStorage } from "../../../hooks/useLocalStorage";
import Button from "@mui/material/Button";

import IconButton from "@mui/material/IconButton";
import { ReactComponent as LockOpenIcon } from "../../../icons/LockOpen24.svg";
import { ReactComponent as SendIcon } from "../../../icons/Send24.svg";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import InputLabel from "@mui/material/InputLabel";

import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";

import DrawerAppBar from "../../../components/DrawerAppBar";
import Title from "../../../components/Title";
import ProgressBar from "../../../components/ProgressBar";

import AccordionTitle from "../../../components/AccordionTitle";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { ReactComponent as ExpandMoreIcon } from "../../../icons/ExpandMore24.svg";

import { doService } from "../../../services/ServiceHelper";
import TableRowNoResults from "../../../components/TableRowNoResults";
import {
  StripedTableCell,
  StripedTableRow,
} from "../../../components/StripedTable";
import {
  getLockedStudents,
  adminUnlockStudent,
  getActivityDateAvailable,
  transferStudentOut,
} from "../../../services/ActivityService";
import { getPeriodName } from "../../../services/SchoolService";
import {
  showOKCancelDialog,
  showToast,
  showError,
  showSuccess,
} from "../../../components/ATPDialogs";
import { clearCacheAll, clearCacheItem } from "../../../services/AdminService";
import { getAllSchools } from "../../../services/SchoolService";

import SelectActivityListsDialog from "../../../components/faculty/SelectActivityListDialog";
import { hasError, getError } from "../../../services/ServiceHelper";
import { Select } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";

const ManageCache = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_results, setResults] = useState([]);

  const [_date, setDate] = useState(dayjs());
  const [_selectedStudent, setSelectedStudent] = useState({});
  const [_availableActivityDates, setAvailableActivityDates] = useState([]);
  const [_dialogActivityOpen, setDialogActivityOpen] = useState(false);
  const [_selectedItem, setSelectedItem] = useState("XX");
  const [_selectedUserEid, setSelectedUserEid] = useState("XX");
  const [_allSchools, setAllSchools] = useState([]);
  const [_selectedSchoolEid, setSelectedSchoolEid] = useState("XX");

  useEffect(() => {
    initSchools();
  }, []);

  const initSchools = () => {
    setAllSchools(getAllSchools());
  };

  const handleSearch = () => {
    const date = _date.format("YYYY-MM-DD");

    setShowProgress(true);

    doService(getLockedStudents(_school, _jwt, date), (results) => {
      setResults(results.data);
      setShowProgress(false);
    });
  };

  const onClickUnlock = (result) => {
    showOKCancelDialog(
      "Confirm Unlock",
      `Click OK to unlock ${result.studentFirstName} ${result.studentLastName} from ${result.activityName}.`,
      () => {
        setShowProgress(true);
        doService(
          adminUnlockStudent(_school, _jwt, result.sadEid),
          (results) => {
            setShowProgress(false);
            showToast("S", "Student Unlocked!");
            handleSearch();
          },
        );
      },
    );
  };

  const onClickTransfer = (result) => {
    // pop up transfer dialog
    setShowProgress(true);
    setSelectedStudent(result);

    // load current list/count of activities
    getActivityDateAvailable(_school, _jwt, result.activityDateEid)
      .then((results) => {
        const labeledActivities = [];
        for (const activity of results.data) {
          let label = `${activity.last_name} - ${activity.name}`;
          labeledActivities.push({
            label: label,
            value: activity.activityDateEid,
          });
        }

        setAvailableActivityDates(labeledActivities);
        setDialogActivityOpen(true);
      })
      .catch((error) => {
        console.log("ERROR:");
        console.log(error);
      })
      .finally(() => {
        setShowProgress();
      });
  };

  const handleActivityListClose = () => {
    setDialogActivityOpen(false);
  };

  const handleTransferOK = (selectedItem) => {
    if (selectedItem === "XX") {
      showError("Unable to Transfer Student", "Please select an activity.");
      return;
    }

    setDialogActivityOpen(false);

    setShowProgress(true);
    transferStudentOut(
      _school,
      _jwt,
      selectedItem.value,
      _selectedStudent.sadEid,
      _selectedStudent.studentEid,
    )
      .then((results) => {
        if (hasError(results) === true) {
          showToast("E", "Unable to Transfer Student: ", getError(results));
          return;
        }

        showToast("S", "Student Transferred");
        handleSearch();
      })
      .catch((error) => {
        console.log("ERROR:");
        console.log(error);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const handleChangeItem = (event) => {
    setSelectedItem(event.target.value);
  };

  const doClearCacheItem = (type, eid) => {
    setShowProgress(true);
    clearCacheItem(_school, _jwt, type, eid)
      .then((results) => {
        if (hasError(results) === true) {
          showError("Unable to Clear Cache", getError(results));
          return;
        }

        showSuccess("Cache Cleared");
      })
      .catch((error) => {
        showError("Unable to Clear Cache", getError(error));
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const handleClickClear = () => {
    console.log("Clear button clicked for item: ", _selectedItem);

    if (_selectedItem === "XX") {
      showError("Unable to Clear Item", "Please select an item to clear.");
      return;
    }

    if (_selectedItem === "EVERY") {
      showOKCancelDialog(
        "Confirm Clear",
        "Click OK to clear everything.",
        clearEverything,
      );
    } else if (_selectedItem === "SCH") {
      if (_selectedSchoolEid === "XX") {
        showError("Unable to Clear School Cache", "Please select a school.");
        return;
      }
      showOKCancelDialog(
        "Confirm Clear",
        "Click OK to clear the school cache.",
        doClearCacheItem.bind(null, "SCH", _selectedSchoolEid),
      );
    } else if (_selectedItem === "ACT") {
      showOKCancelDialog(
        "Confirm Clear",
        "Click OK to clear the activities cache.",
        doClearCacheItem.bind(null, "ACT", _selectedSchoolEid),
      );
    } else if (_selectedItem === "U") {
      showOKCancelDialog(
        "Confirm Clear",
        "Click OK to clear the users cache.",
        doClearCacheItem.bind(null, "U", _selectedSchoolEid),
      );
    }
  };

  const clearEverything = () => {
    setShowProgress(true);
    clearCacheAll(_school, _jwt)
      .then((results) => {
        if (hasError(results) === true) {
          showError("Unable to Clear Cache", getError(results));
          return;
        }

        showSuccess("Cache Cleared");
      })
      .catch((error) => {
        showError("Unable to Clear Cache", getError(error));
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const handleChangeSchool = (event) => {
    setSelectedSchoolEid(event.target.value);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Manage Cache" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Accordion defaultExpanded sx={{ mt: 1 }} expanded>
          <AccordionSummary className="accordionHeader">
            <AccordionTitle text="Clear School" />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <InputLabel sx={{ mt: 1 }}>Select Item to Clear</InputLabel>
                <Select
                  sx={{ mt: 0, pt: 0 }}
                  size="small"
                  onChange={handleChangeItem}
                  value={_selectedItem}
                  fullWidth
                >
                  <MenuItem key={"XX"} value={"XX"}>
                    -- SELECT --
                  </MenuItem>
                  <MenuItem key={"EVERY"} value={"EVERY"}>
                    EVERYTHING
                  </MenuItem>
                  <MenuItem key={"SCH"} value={"SCH"}>
                    SCHOOL
                  </MenuItem>
                  <MenuItem key={"ACT"} value={"ACT"}>
                    ACTIVITIES
                  </MenuItem>
                  <MenuItem key={"U"} value={"U"}>
                    USERS
                  </MenuItem>
                </Select>
              </Grid>

              {_selectedItem === "SCH" && (
                <Grid item xs={3}>
                  <InputLabel sx={{ mt: 1 }}>Select School to Clear</InputLabel>
                  <Select
                    sx={{ mt: 0, pt: 0 }}
                    size="small"
                    onChange={handleChangeSchool}
                    value={_selectedSchoolEid}
                    fullWidth
                  >
                    <MenuItem key={"XX"} value={"XX"}>
                      -- SELECT --
                    </MenuItem>
                    {_allSchools.map((school) => (
                      <MenuItem key={school.eid} value={school.eid}>
                        {school.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}
              <Grid item xs={3}></Grid>
              <Grid item xs={3}></Grid>
            </Grid>
            <Button
              sx={{ mt: 1 }}
              variant="contained"
              onClick={handleClickClear}
            >
              Clear
            </Button>
            <Button
              sx={{ mt: 1, ml: 1 }}
              variant="outlined"
              className="pull-right"
            >
              Clear All
            </Button>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ mt: 1 }} expanded>
          <AccordionSummary
            className="accordionHeader"
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionTitle text="Search Criteria" />
          </AccordionSummary>
          <AccordionDetails>
            <InputLabel>Date *</InputLabel>
            <MobileDatePicker
              slotProps={{ textField: { size: "small" } }}
              value={_date}
              onChange={(newValue) => {
                setDate(newValue);
              }}
            />

            <Button
              sx={{ mt: 1 }}
              variant="contained"
              fullWidth
              onClick={handleSearch}
            >
              Search
            </Button>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ mt: 1 }}>
          <AccordionSummary
            className="accordionHeader"
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionTitle text={`Search Results (${_results.length})`} />
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <StripedTableRow>
                  <StripedTableCell>Student Name</StripedTableCell>
                  <StripedTableCell>Activity</StripedTableCell>
                  <StripedTableCell align="center">Period</StripedTableCell>
                  <StripedTableCell>Locked By</StripedTableCell>
                  <StripedTableCell align="center">Actions</StripedTableCell>
                </StripedTableRow>
              </TableHead>
              <TableBody>
                <TableRowNoResults data={_results} />
                {_results.map((result) => (
                  <StripedTableRow>
                    <StripedTableCell>
                      {result.studentLastName}, {result.studentFirstName}
                      <br />({result.grade})
                    </StripedTableCell>
                    <StripedTableCell>{result.activityName}</StripedTableCell>
                    <StripedTableCell align="center">
                      {getPeriodName(_school, result.period - 1)}
                    </StripedTableCell>
                    <StripedTableCell>
                      {result.teacherLastName}, {result.teacherFirstName}
                    </StripedTableCell>
                    <StripedTableCell align="center">
                      <IconButton
                        color="inherit"
                        onClick={() => onClickUnlock(result)}
                        title="click to unlock student but keep in activity"
                      >
                        <LockOpenIcon />
                      </IconButton>{" "}
                      <IconButton
                        color="inherit"
                        onClick={() => onClickTransfer(result)}
                        title="click to unlock student and transfer to another activity"
                      >
                        <SendIcon />
                      </IconButton>{" "}
                    </StripedTableCell>
                  </StripedTableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </Container>

      <SelectActivityListsDialog
        title="Transfer Student to Another Activity"
        open={_dialogActivityOpen}
        onClose={handleActivityListClose}
        onOK={handleTransferOK}
        activities={_availableActivityDates}
      />
    </>
  );
};

export default ManageCache;
